import { useQuery } from '@tanstack/react-query'
import { Navigate, useOutletContext, useParams } from 'react-router'

import { taxonomiasDelTipo, useGruposDeUnNuevo, useTipoDeContenido } from '../api/estructura'
import { consulta, pedir } from '../api/nucleo'
import { Aviso, Esqueleto } from '../componentes/ui'
import { Editor, type PestanaExtra } from '../motor/Editor'
import { campoPorNombre, useEsquema } from '../motor/esquema'
import { Listado } from '../motor/Listado'
import type { Contexto } from '../paginas/Modulo'
import { PestanaDeGrupo } from '../paginas/PestanaDeGrupo'
import { PestanaSeo } from '../paginas/PestanaSeo'
import { TerminosPorTaxonomia } from './TerminosPorTaxonomia'

/**
 * `…/contenido/<id>`: lleva al editor de un contenido dentro de su tipo, cuando quien enlaza sabe
 * el id del contenido pero no el de su tipo («Publicar mi web», un aviso).
 */
export const IrAlContenido = () => {
  const { id } = useParams()
  const { espacio, sitio } = useOutletContext<Contexto>()
  const valido = id !== undefined && /^\d+$/.test(id)
  const { data, isError } = useQuery({
    queryKey: ['doc', 'contenidos', 'tipo-de', id],
    enabled: valido,
    queryFn: () => pedir<{ tipo: number | { id: number } }>(`/contenidos/${id}${consulta({ depth: 0, draft: true })}`),
  })
  if (!sitio || !valido || isError) return <Aviso tono="error">Ese contenido no existe, o no es tuyo.</Aviso>
  if (!data) return <Esqueleto filas={6} />
  const tipo = typeof data.tipo === 'number' ? data.tipo : data.tipo.id
  return <Navigate replace to={`/e/${espacio}/s/${sitio.id}/contenidos/${tipo}/${id}`} />
}

/**
 * Los contenidos de un tipo (plan del CMS dinámico §2.5 y §6): «Propiedades», «Cursos»… Cada
 * tipo activo de la web tiene su entrada en el menú, que abre esta pantalla: el listado de
 * `contenidos` de ese tipo y su editor, con el nombre del tipo en todas partes.
 *
 * Lo que enseña el editor lo dice el tipo, que es un dato del núcleo: qué campos de siempre
 * usa (`camposBase`), si un contenido cuelga de otro (`jerarquico`) y con qué taxonomías se
 * clasifica. Los campos a medida son los grupos que el núcleo dice que aplican (una pestaña
 * cada uno, como en los productos). El tipo se pone al crear y no cambia.
 */
export const PaginaDeContenidos = () => {
  const { tipo: tipoEnLaRuta, id } = useParams()
  const { espacio, sitio, permisos } = useOutletContext<Contexto>()
  const idDelTipo = tipoEnLaRuta && /^\d+$/.test(tipoEnLaRuta) ? Number(tipoEnLaRuta) : null
  const { data: tipo, isPending, isError } = useTipoDeContenido(idDelTipo)
  const { data: esquema } = useEsquema('contenidos')
  // Las opciones de «Campos de siempre» del tipo salen de su esquema: qué campos del contenido puede esconder un tipo.
  const { data: esquemaDelTipo } = useEsquema('tipos-de-contenido')
  const idNumerico = id !== undefined && /^\d+$/.test(id) ? Number(id) : null
  // Los grupos de campos que aplican: para uno guardado, con el esquema y su id; para uno nuevo, con su tipo ya puesto.
  const { data: esquemaConGrupos } = useEsquema('contenidos', { id: idNumerico, enabled: idNumerico !== null })
  const { data: gruposDelNuevo } = useGruposDeUnNuevo('contenidos', sitio?.id ?? null, { tipo: idDelTipo }, id === 'nuevo' && idDelTipo !== null)

  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (!(sitio.modulos ?? []).includes('estructura')) return <Aviso tono="error">Esta web no tiene tipos de contenido. Pídeselo al equipo si los necesitas.</Aviso>
  if (idDelTipo === null || isError) return <Aviso tono="error">Ese tipo de contenido no existe, o no es de esta web.</Aviso>
  if (isPending || !tipo) return <Esqueleto filas={8} />

  const base = `/e/${espacio}/s/${sitio.id}/contenidos/${tipo.id}`
  const etiquetas = { singular: tipo.nombre, plural: tipo.nombrePlural }

  if (id === undefined) {
    return (
      <Listado
        base={base}
        // La columna del tipo sobra: todos son de este.
        columnas={esquema?.columnas.filter((c) => c !== 'tipo')}
        coleccion="contenidos"
        descripcion={tipo.descripcion || `Los contenidos de tipo «${tipo.nombre}» de tu web. Publícalos cuando estén listos.`}
        espacio={espacio}
        etiquetas={etiquetas}
        puedeCrear={permisos.tiene('contenido.crear')}
        sitio={sitio.id}
        vacio={`Todavía no hay ninguno. Crea el primero: si lo dejas en borrador, no sale en la web hasta que lo publiques.`}
        where={{ 'where[tipo][equals]': String(tipo.id) }}
      />
    )
  }

  if (id !== 'nuevo' && idNumerico === null) return <Aviso tono="error">Ese contenido no existe, o no es tuyo.</Aviso>

  const opcionales = (campoPorNombre(esquemaDelTipo?.campos ?? [], 'camposBase')?.opciones ?? []).map((o) => o.valor)
  const elegidos = tipo.camposBase ?? []
  const taxonomias = taxonomiasDelTipo(tipo)
  const grupos = idNumerico !== null ? (esquemaConGrupos?.grupos ?? []) : (gruposDelNuevo?.grupos ?? [])

  const pestanas: PestanaExtra[] = []
  if (taxonomias.length > 0) {
    pestanas.push({ clave: 'clasificacion', etiqueta: 'Clasificación', campos: ['terminos'], contenido: () => <TerminosPorTaxonomia taxonomias={taxonomias} /> })
  }
  for (const grupo of grupos) {
    pestanas.push({ clave: `grupo-${grupo.id}`, etiqueta: grupo.pestana, campos: ['datos'], contenido: ({ coleccion, id: idDoc }) => <PestanaDeGrupo coleccion={coleccion} grupo={grupo} id={idDoc} /> })
  }
  pestanas.push({ clave: 'seo', etiqueta: 'SEO', campos: ['seo'], contenido: ({ coleccion, id: idDoc }) => <PestanaSeo coleccion={coleccion} id={idDoc} /> })

  return (
    <Editor
      base={base}
      coleccion="contenidos"
      espacio={espacio}
      etiquetas={etiquetas}
      id={idNumerico}
      // Nace con su tipo puesto, y el tipo no cambia (lo comprueba el núcleo): no se ofrece.
      inicialExtra={{ tipo: tipo.id }}
      ocultarCampos={['tipo', 'datos', ...opcionales.filter((c) => !elegidos.includes(c)), ...(tipo.jerarquico ? [] : ['padre'])]}
      pestanasExtra={pestanas}
      sitio={sitio.id}
    />
  )
}
